import axios from "axios";
import { apiConfig } from "src/config";
import { API_COIN } from "src/constants";

const coin = API_COIN;

const API = axios.create({
    baseURL: apiConfig.baseURL,
    timeout: 99000,
});

export const fetcher = async (url, getToken) => {
    const { accessToken } = await getToken();

    return API.get(url, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    })
        .then(res => res.data)
        .catch(error => {
            throw error;
        });
};

export const getAuditLogV2 = (
    accessToken,
    startDate,
    endDate,
    pageNumber,
    pageSize,
    sort,
    activityType,
    userEmail,
    subAccountName,
    groupIdsList,
    hideAuthLogs
) => {
    let params = "";
    if (activityType) params = params + "activityType=" + activityType + "&";
    if (userEmail) params = params + "userEmail=" + userEmail.replaceAll("+", "%2B") + "&";
    if (subAccountName) params = params + "subAccountName=" + subAccountName + "&";
    if (hideAuthLogs) params = params + "hideAuthLogs=true&";
    return API.get(
        `v2/activity_log?${params}startDateUnixMs=${startDate}&endDateUnixMs=${endDate}&coin=${coin}&sort=${sort}&pageNumber=${pageNumber}&pageSize=${pageSize}${
            groupIdsList?.length
                ? `&${groupIdsList.map(i => `groupIdsList=${i.group.id}`).join("&")}`
                : ""
        }`,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );
};

export const getActivityTypes = accessToken => {
    return API.get("/activity_log/activity_types", {
        headers: { Authorization: "Bearer " + accessToken },
    });
};

export const getSubAccountHourlyHashrate = (subAccount, startDate, endDate, accessToken) =>
    API.get(
        "subaccount_hashrate_hour/" +
            subAccount +
            "?coin=" +
            coin +
            "&startDateUnixMs=" +
            startDate +
            "&endDateUnixMs=" +
            endDate,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

export const getCumulatedSubAccountHourlyHashrate = (
    startDate,
    endDate,
    accessToken,
    groupIdsList
) =>
    API.get(
        `cumulated_subaccount_hashrate_hour?coin=${coin}&startDateUnixMs=${startDate}&endDateUnixMs=${endDate}
  ${
      groupIdsList.length ? `&${groupIdsList.map(i => `groupIdsList=${i.group.id}`).join("&")}` : ""
  }`,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

export const getWorkerHourlyHashrate = (subAccount, workerId, startDate, endDate, accessToken) =>
    API.get(
        "worker_hashrate_hour/" +
            subAccount +
            "/" +
            workerId +
            "?coin=" +
            coin +
            "&startDateUnixMs=" +
            startDate +
            "&endDateUnixMs=" +
            endDate,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

export const getSubAccountStats = (subAccount, accessToken, tagName) =>
    API.get("subaccount_stats/" + subAccount + "?coin=" + coin + "&tagName=" + tagName, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const getCumulatedSubAccountStats = (accessToken, groupIdsList) =>
    API.get(
        `cumulated_granted_subaccount_stats?coin=${coin}${
            groupIdsList.length
                ? `&${groupIdsList.map(i => `groupIdsList=${i.group.id}`).join("&")}`
                : ""
        }`,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

export const getSubAccountDailyHashrate = (subAccount, startDate, endDate, accessToken) =>
    API.get(
        "subaccount_hashrate_day/" +
            subAccount +
            "?coin=" +
            coin +
            "&start=" +
            startDate +
            "&end=" +
            endDate,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

export const getCumulatedSubAccountDailyHashrate = (
    startDate,
    endDate,
    accessToken,
    groupIdsList
) =>
    API.get(
        `cumulated_subaccount_hashrate_day?coin=${coin}&start=${startDate}&end=${endDate}${
            groupIdsList.length
                ? `&${groupIdsList.map(i => `groupIdsList=${i.group.id}`).join("&")}`
                : ""
        }`,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

export const getWorkerDailyHashrate = (subAccount, workerId, startDate, endDate, accessToken) =>
    API.get(
        "worker_hashrate_day/" +
            subAccount +
            "/" +
            workerId +
            "?coin=" +
            coin +
            "&start=" +
            startDate +
            "&end=" +
            endDate,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

export const getWorkers = (
    subAccount,
    sort,
    status,
    accessToken,
    tag,
    pageNumber,
    pageSize,
    workerNameSearchStr
) =>
    API.get(
        "workers/" +
            subAccount +
            "?coin=" +
            coin +
            "&sort=" +
            sort +
            "&status=" +
            status +
            "&tag=" +
            tag +
            "&pageNumber=" +
            pageNumber +
            "&pageSize=" +
            pageSize +
            "&workerNameSearchStr=" +
            encodeURIComponent(workerNameSearchStr),
        { headers: { Authorization: "Bearer " + accessToken } }
    );

export const viewerGetSubAccountHourlyHashrate = (subAccount, startDate, endDate, apiKey) =>
    API.get(
        "subaccount_hashrate_hour/" +
            subAccount +
            "?coin=" +
            coin +
            "&startDateUnixMs=" +
            startDate +
            "&endDateUnixMs=" +
            endDate,
        {
            headers: { "X-API-KEY": apiKey },
        }
    );

export const viewerGetWorkerHourlyHashrate = (subAccount, workerId, startDate, endDate, apiKey) =>
    API.get(
        "worker_hashrate_hour/" +
            subAccount +
            "/" +
            workerId +
            "?coin=" +
            coin +
            "&startDateUnixMs=" +
            startDate +
            "&endDateUnixMs=" +
            endDate,
        {
            headers: { "X-API-KEY": apiKey },
        }
    );

export const viewerGetSubAccountStats = (subAccount, apiKey, tagName) =>
    API.get("subaccount_stats/" + subAccount + "?coin=" + coin + "&tagName=" + tagName, {
        headers: { "X-API-KEY": apiKey },
    });

export const viewerGetSubAccountDailyHashrate = (subAccount, startDate, endDate, apiKey) =>
    API.get(
        "subaccount_hashrate_day/" +
            subAccount +
            "?coin=" +
            coin +
            "&start=" +
            startDate +
            "&end=" +
            endDate,
        {
            headers: { "X-API-KEY": apiKey },
        }
    );

export const viewerGetWorkerDailyHashrate = (subAccount, workerId, startDate, endDate, apiKey) =>
    API.get(
        "worker_hashrate_day/" +
            subAccount +
            "/" +
            workerId +
            "?coin=" +
            coin +
            "&start=" +
            startDate +
            "&end=" +
            endDate,
        {
            headers: { "X-API-KEY": apiKey },
        }
    );

export const viewerGetWorkers = (
    subAccount,
    sort,
    status,
    apiKey,
    tag,
    pageNumber,
    pageSize,
    workerNameSearchStr
) =>
    API.get(
        "workers/" +
            subAccount +
            "?coin=" +
            coin +
            "&sort=" +
            sort +
            "&status=" +
            status +
            "&tag=" +
            tag +
            "&pageNumber=" +
            pageNumber +
            "&pageSize=" +
            pageSize +
            "&workerNameSearchStr=" +
            encodeURIComponent(workerNameSearchStr),
        {
            headers: { "X-API-KEY": apiKey },
        }
    );

export const getCoins = () => API.get("coins");

/**
 * Fetches all the sub accounts in the pool
 * @param {*} accessToken
 * @param {*} coin
 */
export const getAllSubAccounts = (accessToken, coin) =>
    API.get("subaccount_stats?coin=" + coin, {
        headers: { Authorization: "Bearer " + accessToken },
    });

/**
 * Gets all the sub accounts' stats owned by a user/account
 * @param {*} accessToken
 * @param {*} coin
 * @param {*} groupIdsList
 * @param {*} archived
 */
export const getAllSubAccountsAssociatedWithAUser = (accessToken, groupIdsList, archived) =>
    API.get(
        `v2/granted_subaccount_stats?coin=${coin}&archived=${archived}${
            groupIdsList.length
                ? `&${groupIdsList.map(i => `groupIdsList=${i.group.id}`).join("&")}`
                : ""
        }`,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

/**
 * Gets all the sub accounts' stats owned by a user/account
 * @param {*} accessToken
 */

export const getGrantedSubAccounts = accessToken =>
    API.get("granted_subaccounts", {
        headers: { Authorization: "Bearer " + accessToken },
    });

/**
 * Fetches the blocks mined by the pool
 * @param {*} coin
 * @param {*} accessToken
 */
export const getBlocks = (accessToken, startDate, pageNumber, pageSize, sort) =>
    API.get(
        "blocks?coin=" +
            coin +
            "&startDateUnixMs=" +
            startDate +
            "&pageNumber=" +
            pageNumber +
            "&pageSize=" +
            pageSize +
            "&sort=" +
            sort,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

export const getTransactions = (subAccount, accessToken, startDate, endDate) =>
    API.get(
        "transactions/" +
            subAccount +
            "?coin=" +
            coin +
            "&startDateUnixMs=" +
            startDate +
            "&endDateUnixMs=" +
            endDate,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

export const getEarnings = (subAccount, accessToken, startDate, endDate) =>
    API.get(
        "v2/earnings/" +
            subAccount +
            "?coin=" +
            coin +
            "&startDateUnixMs=" +
            startDate +
            "&endDateUnixMs=" +
            endDate,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

export const getPoolHourlyHashrate = (startDate, endDate, accessToken) =>
    API.get(
        "pool_hashrate_hour?coin=" +
            coin +
            "&startDateUnixMs=" +
            startDate +
            "&endDateUnixMs=" +
            endDate,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

export const getPoolDailyHashrate = (startDate, endDate, accessToken) =>
    API.get("pool_hashrate_day?coin=" + coin + "&start=" + startDate + "&end=" + endDate, {
        headers: { Authorization: "Bearer " + accessToken },
    });

/**
 * Fetches the statistics of the pool (Workers, Avg Hashrate)
 * @param {*} coin
 * @param {*} accessToken
 */
export const getPoolStats = accessToken =>
    API.get("pool_stats?coin=" + coin, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const getSubAccountBalance = (subAccount, accessToken) =>
    API.get("balance/" + subAccount + "?coin=" + coin, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const getLoggedInUser = accessToken =>
    API.get("v2/users/logged-in-user", {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const getAutoWithdrawalParams = (subAccount, accessToken) =>
    API.get("/settings/autoWithdrawParams/" + subAccount + "?coin=" + coin, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const getActiveWithdrawalAddress = (subAccount, accessToken) =>
    API.get("/wallets/autoWithdrawalAddress/" + subAccount + "?coin=" + coin, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const getWithdrawalAddresses = (subAccount, accessToken) =>
    API.get("/wallets/addresses/" + subAccount + "?coin=" + coin, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const getSubAccountsLabels = accessToken =>
    API.get("/subaccount_labels", {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const updateSubAccountsLabels = (accessToken, payload) =>
    API({
        method: "POST",
        url: "/subaccount_labels",
        data: payload,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const addWithdrawalAddress = (subAccount, addressLabel, address, accessToken) =>
    API({
        method: "PUT",
        url: "/wallets/addresses/" + subAccount + "/" + addressLabel + "/" + address,
        params: {
            coin: coin,
        },
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const setActiveWithdrawalAddress = (subAccount, address, accessToken) =>
    API({
        method: "PUT",
        url: "/wallets/autoWithdrawalAddress/" + subAccount + "/" + address,
        params: {
            coin: coin,
        },
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const deactivateWithdrawalAddress = (subAccount, address, accessToken) =>
    API({
        method: "PUT",
        url: "/wallets/deactivateAutoWithdrawal/" + subAccount + "/" + address,
        params: {
            coin: coin,
        },
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const setAutoWithdrawParams = (subAccount, auto, accessToken) =>
    API({
        method: "PUT",
        url: "/settings/autoWithdrawParams/" + subAccount + "/" + coin,
        params: {
            auto: auto,
        },
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const deleteWorkers = (subAccount, accessToken, workersIdList, lastShareTime) =>
    API.delete(
        `workers/${subAccount}/${coin}${
            workersIdList.length ? "" : `?lastShareTime=${lastShareTime}`
        }`,
        {
            headers: {
                Authorization: "Bearer " + accessToken,
            },

            data: workersIdList,
        }
    );

export const createSubAccountAndAddWithdrawalAddressV2 = (
    subAccountName,
    groupId,
    addressLabel,
    address,
    userSubAccountAccess,
    accessToken
) => {
    if (userSubAccountAccess !== null) {
        return API({
            method: "POST",
            url:
                "v2/sub_account/" +
                subAccountName +
                "/group/" +
                groupId +
                "/" +
                addressLabel +
                "/" +
                address,
            params: {
                coin: coin,
            },
            data: { userSubAccountAccess },
            headers: {
                Authorization: "Bearer " + accessToken,
            },
        });
    } else {
        return API({
            method: "POST",
            url:
                "v2/sub_account/" +
                subAccountName +
                "/group/" +
                groupId +
                "/" +
                addressLabel +
                "/" +
                address,
            params: {
                coin: coin,
            },
            headers: {
                Authorization: "Bearer " + accessToken,
            },
        });
    }
};

export const authenticate = apiKey =>
    API.get("/authentication_test", {
        headers: { "X-API-KEY": apiKey },
    });

export const getAPIKeys = (subAccount, role, accessToken) =>
    API.get("/api_keys/" + subAccount + "?role=" + role, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const createAPIKey = (subAccount, linkName, role, accessToken) =>
    API({
        method: "PUT",
        url: "/api_keys/" + subAccount,
        params: {
            keyName: linkName,
            role: role,
        },
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const deleteAPIKey = (subAccount, apiKey, accessToken) =>
    API.delete("api_keys/" + subAccount + "/" + apiKey, {
        headers: { Authorization: "Bearer " + accessToken },
        // data: payload
    });

export const getSubAccountFinancials = (accessToken, startDate, endDate) =>
    API.get(
        "financial_stats?coin=" +
            coin +
            "&startDateUnixMs=" +
            startDate +
            "&endDateUnixMs=" +
            endDate,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

export const getFinancialStatsForSubAccounts = (accessToken, startDate, endDate, groupIdsList) =>
    API.get(
        "financial_overview?coin=" +
            coin +
            "&startDateUnixMs=" +
            startDate +
            "&endDateUnixMs=" +
            endDate +
            `${
                groupIdsList.length
                    ? `&${groupIdsList.map(i => `groupIdsList=${i}`).join("&")}`
                    : ""
            }`,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

export const getWorkerTags = (subAccount, accessToken) =>
    API.get("/tags/" + coin + "/" + subAccount, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const viewerGetWorkerTags = (subAccount, apiKey) =>
    API.get("/tags/" + coin + "/" + subAccount, {
        headers: { "X-API-KEY": apiKey },
    });

export const createWorkerTag = (subAccount, tagName, accessToken) =>
    API({
        method: "PUT",
        url: "/tags/" + coin + "/" + subAccount + "/" + tagName,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const deleteWorkerTag = (subAccount, tagName, accessToken) =>
    API.delete("/tags/" + coin + "/" + subAccount + "/" + tagName, {
        headers: { Authorization: "Bearer " + accessToken },
        // data: payload
    });

export const tagWorkers = (subAccount, tagName, accessToken, payload) =>
    API({
        method: "PUT",
        url: "/tags/tag/" + coin + "/" + subAccount + "/" + tagName,
        data: payload,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const untagWorkers = (subAccount, accessToken, payload) =>
    API.delete("/tags/untag/" + coin + "/" + subAccount, {
        headers: { Authorization: "Bearer " + accessToken },
        data: payload,
    });

export const createNewSubAccountAlert = (
    subAccount,
    hashrateThreshold,
    activeWorkersThreshold,
    payload,
    accessToken
) => {
    var url = `/alerts/addSubAccount/${coin}/${subAccount}?activeWorkersThreshold=${activeWorkersThreshold}&hashrateThreshold=${hashrateThreshold}`;

    return API({
        method: "POST",
        url: url,
        data: payload,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });
};

export const updateSubAccountAlert = (
    alertId,
    subAccountName,
    hashrateThreshold,
    activeWorkersThreshold,
    payload,
    accessToken
) => {
    var url = `/alerts/updateSubAccountAlert/${coin}/${subAccountName}/${alertId}?hashrateThreshold=${hashrateThreshold}&activeWorkersThreshold=${activeWorkersThreshold}`;

    return API({
        method: "PUT",
        url: url,
        data: payload,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });
};

export const deleteAlert = (subAccount, alertId, accessToken) =>
    API.delete("/alerts/" + coin + "/" + subAccount + "/" + alertId, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const getSubAccountAlerts = (accessToken, sort, groupIdsList, triggeredOnly) =>
    API.get(
        `/alerts/subaccount_alerts?coinString=${coin}&sort=${sort}&triggeredOnly=${triggeredOnly}${
            groupIdsList.length
                ? `&${groupIdsList.map(i => `groupIdsList=${i.group.id}`).join("&")}`
                : ""
        }`,
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );

export const getRoleInfo = (subAccount, accessToken) =>
    API.get("/get_role/" + subAccount, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const createGroup = (groupName, accessToken) =>
    API({
        method: "PUT",
        url: "/group/" + groupName,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const getAllUsersAssociatedWithAGroupV2 = accessToken =>
    API.get("v2/users/users-by-groups", {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const getAllGroupsAndUsersV2 = accessToken =>
    API.get("v2/user-groups", {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const addUserToMultipleGroups = (accessToken, bodyFormData) =>
    API({
        method: "POST",
        url: "v2/users",
        data: bodyFormData,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const deleteUserFromGroupV2 = (userId, accessToken, groupId) =>
    API.delete(`v2/users/${userId}?groupIdsList=${groupId}`, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const editFeeRateForGroup = (groupId, accessToken, payload) =>
    API({
        method: "PUT",
        url: "fee/" + groupId,
        data: payload,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const updateUserRoleNameV2 = (userId, groupId, updatedRole, accessToken) =>
    API({
        method: "PUT",
        url: "v2/users/" + userId + "/group/" + groupId + "/role/" + updatedRole,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const getWorkerCounts = (subAccount, tag, accessToken) =>
    API.get("/workers/workerCounts/" + subAccount + "?coin=" + coin + "&tagName=" + tag, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const viewerGetWorkerCounts = (subAccount, tag, apiKey) =>
    API.get("/workers/workerCounts/" + subAccount + "?coin=" + coin + "&tagName=" + tag, {
        headers: { "X-API-KEY": apiKey },
    });

export const getTaggedWorkerCount = (subAccount, accessToken) =>
    API.get("/tags/taggedWorkerCount/" + coin + "/" + subAccount, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const viewerGetTaggedWorkerCount = (subAccount, apiKey) =>
    API.get("/tags/taggedWorkerCount/" + coin + "/" + subAccount, {
        headers: { "X-API-KEY": apiKey },
    });

export const archiveSubAccount = (subAccount, accessToken) =>
    API({
        method: "PUT",
        url: `/sub_account/archive/${subAccount}`,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const unarchiveSubAccount = (subAccount, accessToken) =>
    API({
        method: "PUT",
        url: `/sub_account/unarchive/${subAccount}`,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const saveAutoWithdrawal = (subAccount, accessToken, walletAllocationRequestList) =>
    API({
        method: "POST",
        url: "/wallets/saveAutoWithdrawalDetails/" + subAccount + "/" + coin,
        data: walletAllocationRequestList,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const approvalRequest = (approvalRequestId, accessToken, action) =>
    API({
        method: "POST",
        url: "/approvals/" + approvalRequestId,
        data: { action: action },
        headers: {
            Authorization: "Bearer " + accessToken,
        },
    });

export const getPendingRequests = (accessToken, userId) => {
    return API.get(
        userId ? `/approvals/pending_requests/${userId}` : "/approvals/pending_requests",
        {
            headers: { Authorization: "Bearer " + accessToken },
        }
    );
};

export const updateApprovalThreshold = (accessToken, groupName, newApprovalThreshold) => {
    return API({
        method: "PUT",
        url: "/group/" + groupName + "/update_approval_threshold/" + newApprovalThreshold,
        headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "text/plain",
        },
    });
};

export const addUserSubAccountRoles = (userId, subAccountObj, accessToken) =>
    API({
        method: "PUT",
        url: `/users/${userId}/sub-accounts`,
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
        data: subAccountObj,
    });

export const addUserToSubAccount = (userId, subAccountRoleInfo, accessToken) =>
    API({
        method: "PUT",
        url: `/users/${userId}/sub-accounts`,
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
        data: subAccountRoleInfo,
    });

export const updateUserSubAccountRole = (userId, subAccountName, subAccountRoleName, accessToken) =>
    API({
        method: "PUT",
        url: `/users/${userId}/sub-account_role/${subAccountName}/${subAccountRoleName}`,
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });

export const deleteUserSubAccountRole = (userId, subAccountName, accessToken) =>
    API({
        method: "DELETE",
        url: `/users/${userId}/sub-account_role/${subAccountName}`,
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });

export const getResetUserMFA = (userId, accessToken) =>
    API.get("/users/resetMFA/" + userId, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const checkIfUserExistsByEmail = (userEmail, accessToken) =>
    API.get("/users/get-mutual-groups?userEmail=" + encodeURIComponent(userEmail), {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const getSubAccountFinancialsReport = (
    startDate,
    endDate,
    subAccounts,
    timeInterval,
    accessToken
) =>
    API({
        method: "POST",
        url: `subaccount_financials_report?start=${startDate}&end=${endDate}&timeInterval=${timeInterval}`,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
        data: subAccounts,
    });

export const getSubAccountWorkersReport = (
    startDate,
    endDate,
    subAccounts,
    pageNumber,
    pageSize,
    sortOrder,
    accessToken
) =>
    API({
        method: "POST",
        url: `worker_hashrate_day?start=${startDate}&end=${endDate}&pageNumber=${pageNumber}&pageSize=${pageSize}&sort=${sortOrder}`,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
        data: subAccounts,
    });

export const getSubAccountHashrateReport = (
    startDate,
    endDate,
    subAccounts,
    timeInterval,
    accessToken
) =>
    API({
        method: "POST",
        url: `subaccount_hashrate_report?start=${startDate}&end=${endDate}&timeInterval=${timeInterval}`,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
        data: subAccounts,
    });

export const getSubAccountWalletReport = (subAccounts, accessToken) =>
    API({
        method: "POST",
        url: `wallets/addresses`,
        headers: {
            Authorization: "Bearer " + accessToken,
        },
        data: subAccounts,
    });

export const getCurrentSubaccountHashrate = accessToken =>
    API.get("/current_subaccount_hashrate", {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const getCurrentSubaccountWorkerCount = accessToken =>
    API.get("/current_subaccount_worker_count", {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const getCurrentLocationStats = accessToken =>
    API.get("/current_location_stats", {
        headers: { Authorization: "Bearer " + accessToken },
    });

export const acceptTosVersion = (groupId, tosVersion, accessToken) =>
    API({
        method: "PUT",
        url: `/group/${groupId}/accept_tos_version/?tosVersion=${tosVersion}`,
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });

export const getDailyBlocksCounts = (startDate, endDate, accessToken) =>
    API.get(`/block_count_per_day?startDateUnixMs=${startDate}&endDateUnixMs=${endDate}`, {
        headers: { Authorization: "Bearer " + accessToken },
    });

export default API;
